<template>
	<div class="container__filtrografico">
		<InputSearch
			v-model="textFilter"
			style="margin-bottom: 1rem"
			:placeholder="$t('search') + '...'"
		/>
		<label class="container__filtrografico__selecionartodas">
			<input
				type="checkbox"
				:checked="todasSelecionadas"
				@change="onSelecionarTodas($event)"
			/>
			<span>{{ $t('selectAll') }}</span>
		</label>
		<div class="container__filtrografico__lista">
			<div
				v-for="(dadosIndividual, index) in dadosPaginados"
				:key="index"
				class="container__filtrografico__item"
				@click="checkValue(dadosIndividual)"
			>
				<FiltroGraficoFazendaItem
					:color="dadosIndividual.color"
					:label="dadosIndividual.label"
					:valor="dadosIndividual[propriedadeSelecionada]"
					:is-checked="dadosIndividual.isChecked"
					:max-progress-value="valorMaximoBarraDeProgresso"
					:propriedade-selecionada="propriedadeSelecionada"
				/>
			</div>
		</div>
		<div
			v-if="maxPages > 1"
			class="container__filtrografico__paginacao"
		>
			<i
				:class="[
					'fas fa-caret-left container__filtrografico__paginacao__seta',
					{ 'container__filtrografico__paginacao__seta--desabilitada': currentPage === 1 },
				]"
				@click="paginaAnterior"
			/>
			<span class="container__filtrografico__paginacao__info"
				>{{ currentPage }}/{{ maxPages }}</span
			>
			<i
				:class="[
					'fas fa-caret-right container__filtrografico__paginacao__seta',
					{ 'container__filtrografico__paginacao__seta--desabilitada': currentPage === maxPages },
				]"
				@click="proximaPagina"
			/>
		</div>
	</div>
</template>

<script>
import FiltroGraficoFazendaItem from './FiltroGraficoFazendaItem'
import InputSearch from '../../../../../../components/InputSearch/InputSearch'

import _ from 'lodash'

export default {
	components: { FiltroGraficoFazendaItem, InputSearch },
	props: {
		/**
		 * Dados Necessarios para o filtro
		 */
		dadosDoFiltro: Array,
		/**
		 * Propriedade que deve ser selecionada para mostrar como valor
		 * do progresso
		 */
		propriedadeSelecionada: String,
	},
	data: () => {
		return {
			/**
			 * Campo de pesquisa
			 */
			textFilter: '',
			/**
			 * Pagina atual do filtro
			 */
			currentPage: 1,
			/**
			 * Quantidade de fazendas exibidas por pagina no filtro
			 */
			quantityPerPage: 10,
		}
	},
	computed: {
		/**
		 * Dados que foram filtrados pelo campo de pesquisa, ordenado do maior para o menor
		 */
		dadosFiltradosPeloCampoDePesquisa() {
			const dadosFiltrados = this.dadosDoFiltro.filter((f) =>
				f.label.toLowerCase().includes(this.textFilter.toLowerCase()),
			)

			return _.orderBy(dadosFiltrados, [this.propriedadeSelecionada], ['desc'])
		},
		/**
		 * Numero total de paginas
		 */
		maxPages() {
			return Math.max(
				1,
				Math.ceil(this.dadosFiltradosPeloCampoDePesquisa.length / this.quantityPerPage),
			)
		},
		/**
		 * Fatia dos dados filtrados que corresponde a pagina atual
		 */
		dadosPaginados() {
			const startIndex = (this.currentPage - 1) * this.quantityPerPage
			return this.dadosFiltradosPeloCampoDePesquisa.slice(
				startIndex,
				startIndex + this.quantityPerPage,
			)
		},
		/**
		 * Indica se todas as fazendas estao selecionadas (independente do filtro de pesquisa)
		 */
		todasSelecionadas() {
			return this.dadosDoFiltro.length > 0 && this.dadosDoFiltro.every((f) => f.isChecked)
		},
		/**
		 * Retorna todos os valores da propriedade que esta selecionada
		 */
		todosOsValoresDaPropriedadeSelecionada() {
			return this.dadosDoFiltro.map((m) => parseFloat(m[this.propriedadeSelecionada]))
		},
		/**
		 * Valor maximo do progresso
		 */
		valorMaximoBarraDeProgresso() {
			if (this.todosOsValoresDaPropriedadeSelecionada) {
				return Math.max(...this.todosOsValoresDaPropriedadeSelecionada) + 20
			}
			return 0
		},
	},
	watch: {
		/**
		 * Ao pesquisar, volta para a primeira pagina
		 */
		textFilter() {
			this.currentPage = 1
		},
		/**
		 * Garante que a pagina atual nunca ultrapasse o total de paginas
		 */
		maxPages(novoMaximo) {
			if (this.currentPage > novoMaximo) {
				this.currentPage = novoMaximo
			}
		},
	},
	methods: {
		/**
		 * Faz o check do valor para o objeto
		 */
		checkValue(individualValue) {
			individualValue.isChecked = !individualValue.isChecked
			this.$emit('filterClick', individualValue)
		},
		/**
		 * Seleciona ou desmarca todas as fazendas de uma vez
		 */
		onSelecionarTodas(event) {
			this.$emit('selecionarTodas', event.target.checked)
		},
		/**
		 * Volta para a pagina anterior
		 */
		paginaAnterior() {
			if (this.currentPage > 1) {
				this.currentPage--
			}
		},
		/**
		 * Avanca para a proxima pagina
		 */
		proximaPagina() {
			if (this.currentPage < this.maxPages) {
				this.currentPage++
			}
		},
	},
}
</script>

<style scoped>
.container__filtrografico {
	width: 100%;
	height: 100%;
	padding: 1.25rem;
	border: 1px solid rgba(var(--color-rgb-gray), 0.3);
	border-radius: 3px;
	display: flex;
	flex-direction: column;
}

.container__filtrografico__selecionartodas {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 1rem;
	font-size: 0.75rem;
	font-weight: 600;
	cursor: pointer;
	color: rgb(var(--color-gray));
	user-select: none;
}

.container__filtrografico__selecionartodas input {
	cursor: pointer;
}

.container__filtrografico__lista {
	flex: 1;
	overflow-y: auto;
}

.container__filtrografico__item {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	margin-bottom: 1rem;
	font-size: 0.75rem;
	cursor: pointer;
	color: rgb(var(--color-rgb-gray));
}

.container__filtrografico__paginacao {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 1.5rem;
	margin-top: 0.75rem;
}

.container__filtrografico__paginacao__info {
	font-size: 0.9rem;
	font-weight: 600;
	color: rgb(var(--color-gray));
	min-width: 2.5rem;
	text-align: center;
}

.container__filtrografico__paginacao__seta {
	font-size: 1.1rem;
	color: rgb(var(--color-default));
	cursor: pointer;
	transition: transform 0.1s ease-in-out;
}

.container__filtrografico__paginacao__seta:hover {
	transform: scale(1.3);
}

.container__filtrografico__paginacao__seta--desabilitada {
	color: #d7dadf;
	cursor: not-allowed;
}

.container__filtrografico__paginacao__seta--desabilitada:hover {
	transform: scale(1);
}
</style>
